import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import { JournalCard, TacticTab } from './OzonAutomationTactic';
import { MetricChart } from './OzonMetricPanels';
import OzonAutomationRk from './OzonAutomationRk';
import OzonAutomationMilestones from './OzonAutomationMilestones';
import OzonAutomationCoinvest from './OzonAutomationCoinvest';
import OzonAutomationPrices from './OzonAutomationPrices';
import { fmtDate, fmtDateTime } from '../../../../utils/ruDates';
import { matchesSearch } from '../../../../utils/search';

const METRICS = {
  orders: ['Заказы выбранных товаров', 'шт.'], ad_orders: ['Заказы с рекламы выбранных РК', 'шт.'],
  revenue: ['Сумма заказов', '₽'],
  drr_ad: ['ДРР рекламный (расход РК / выручка с рекламы)', '%'], drr_total: ['ДРР общий (вся реклама / вся выручка)', '%'],
  profit: ['Прибыль по начислениям', '₽'], profit_by_order: ['Прибыль по дню заказа', '₽'],
  profit_by_demand: ['Прибыль по дню спроса', '₽'],
  margin_pct: ['Рентабельность', '%'], hits_tocart: ['Корзины товаров (вся воронка)', 'шт.'],
  carts: ['Корзины выбранных РК', 'шт.'], all_ad_spend: ['Вся реклама выбранных товаров', '₽'],
  ad_spend: ['Расход выбранных РК', '₽'], impressions: ['Показы выбранных РК', 'шт.'],
  clicks: ['Клики выбранных РК', 'шт.'], ctr: ['CTR выбранных РК', '%'], cpc: ['Цена клика выбранных РК', '₽'],
  hits_view_search: ['Показы в поиске (общие)', 'шт.'],
  search_position: ['Позиция в поиске (меньше — лучше)', 'место'],
  coinvest_per_unit: ['Соинвест OZON на единицу', '₽'],
  shelf_price: ['Витринная цена (что видит покупатель)', '₽'],
  coinvest_pct: ['Соинвест OZON, %', '%'],
};
const STATES = {
  APPLYING: 'Отправка начата', UNCONFIRMED: 'Нужна сверка с OZON', OBSERVING: 'Наблюдение после изменения',
  AWAITING_CONTENT: 'Ждём выполнения задачи по контенту',
  COMPLETED: 'Наблюдение завершено', INTERRUPTED: 'Проверка прервана',
};
const RESULTS = {
  IMPROVEMENT: 'улучшение', DETERIORATION: 'ухудшение',
  NO_PRACTICAL_CHANGE: 'без значимого изменения', UNCLEAR: 'неясно',
  MIXED: 'смешанный результат',
};
const LEVERS = { BID: 'Ставка', PRICE: 'Цена', SEO: 'SEO', INFOGRAPHIC: 'Инфографика' };
const OUTCOMES = { improve: 'улучшение', deteriorate: 'ухудшение', unclear: 'неясно' };
const SEVERITY = { CRITICAL: 'Критично', WARNING: 'Внимание', INFO: 'Информация' };
const CONFIDENCE = { LOW: 'низкое', MEDIUM: 'среднее', HIGH: 'высокое' };
const EXECUTION_MODES = [
  ['AUTO', 'Автоматический'],
  ['CONTROLLED', 'С подтверждением'],
  ['OBSERVE', 'Наблюдение'],
];
const EXECUTION_MODE_HINT = 'Автоматический — система сама меняет ставку или цену и ведёт журнал. '
  + 'С подтверждением — система готовит изменение, отправляете вы кнопкой «Применить». '
  + 'Наблюдение — только считает и предлагает, в кабинет ничего не отправляет.';
const READY = {
  ACTION: ['Нужно от вас', 'is-action'],
  WAITING: ['Ждём данных', 'is-waiting'],
  OPTIONAL: ['Можно улучшить', 'is-optional'],
  OK: ['Готово', 'is-ok'],
};
const READY_ORDER = ['ACTION', 'WAITING', 'OPTIONAL', 'OK'];

const number = value => value == null ? 'Нет данных' : Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
const percent = value => value == null ? 'Нет данных' : `${Number(value).toFixed(1)}%`;
const failure = err => typeof err?.detail === 'string' ? err.detail : err?.detail?.message || err.message || 'Не удалось получить данные';

// Пояснения занимали больше места, чем то, что они поясняют. Текст никуда не
// делся — он под знаком вопроса рядом с тем, к чему относится.
function Help({ text }) {
  return <i className='oz-auto__help' title={text} aria-label={text}>?</i>;
}

export default function OzonAutomationDashboard({ item, accountId, request, onChanged, initialTab = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  // Одна страница отвечала сразу на все вопросы — «что делать», «как идут
  // дела», «что меняли» — и поэтому ни на один. Вкладки разводят их, а
  // открывается та, ради которой сюда обычно и заходят.
  const [tab, setTab] = useState(initialTab === 'strategy' ? 'log' : (initialTab || 'tactic'));
  const [journalKey, setJournalKey] = useState(0);
  const base = `/api/v1/ozon/${accountId}/bidder/automations/${item.id}`;

  // Обновление сессии пересоздаёт `request`; «Загрузка статистики…» не должна из-за этого заменять открытую вкладку.
  const requestRef = useRef(request);
  requestRef.current = request;
  const load = useCallback(async () => {
    setError('');
    try { setData(await requestRef.current(`${base}/dashboard?days=${days}`)); }
    catch (err) { setError(failure(err)); }
  }, [base, days]);
  useEffect(() => { setData(null); load(); }, [load]);

  const act = async (url, body) => {
    setBusy(true); setError('');
    try {
      const response = await request(`${base}/${url}`, { method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}) });
      setProposal(null); await load();
      setJournalKey(value => value + 1);
      if (onChanged) await onChanged();
      return response;
    } catch (err) { setError(failure(err)); return null; }
    finally { setBusy(false); }
  };

  const pending = data?.actions?.some(action =>
    ['APPLYING', 'UNCONFIRMED', 'OBSERVING', 'AWAITING_CONTENT'].includes(action.status));
  const planner = data?.planner;
  const canAct = item.status === 'ACTIVE' && ['CONTROLLED', 'AUTO'].includes(item.execution_mode);

  // The proposal the cycle produced, turned into the exact request body.
  // A ready hypothesis in observing mode has nowhere to go: rather than show a
  // dead button, offer the switch that makes it usable.
  const changeMode = async mode => {
    if (mode === 'AUTO' && item.execution_mode !== 'AUTO' && !window.confirm(
      'Включить автоматический режим? Система сама будет менять ставки и цены в рамках ограничителей, без вашего подтверждения.'
    )) return;
    setBusy(true); setError('');
    try {
      // The server runs the same checks as saving the settings, so a mode the
      // group is not configured for is refused with the missing field named.
      await request(`${base}/mode?mode=${mode}`, { method: 'PUT' });
      await load();
      if (onChanged) await onChanged();
    } catch (err) { setError(failure(err)); }
    finally { setBusy(false); }
  };
  const enableActing = () => changeMode('CONTROLLED');

  const applyPlanner = () => {
    if (!planner || planner.decision !== 'PROPOSE') return;
    const target = planner.target || {};
    const payload = { action_id: crypto.randomUUID(), lever: planner.lever, target_key: target.key };
    if (planner.lever === 'BID') {
      payload.expected_bid = planner.previous_value;
      payload.requested_bid = planner.requested_value;
    } else if (planner.lever === 'PRICE') {
      payload.sku_id = target.sku_id;
      payload.expected_price = planner.previous_value;
      payload.requested_price = planner.requested_value;
    } else {
      payload.sku_id = target.sku_id;
      payload.reason = planner.reason;
    }
    return act('experiments', payload);
  };

  const saveTurnover = async body => {
    setBusy(true); setError('');
    try {
      await request(`${base}/turnover`, { method: 'PUT', body: JSON.stringify(body) });
      await load();
      if (onChanged) await onChanged();
      return true;
    } catch (err) { setError(failure(err)); return false; }
    finally { setBusy(false); }
  };

  // Калибровка отправляет ставки и цены сразу же, поэтому ответ сервера
  // (или его отказ с причиной) показывается, а данные перечитываются.
  const calibration = async method => {
    setBusy(true); setError('');
    try {
      await request(`${base}/calibration`, { method });
      await load();
      setJournalKey(value => value + 1);
    } catch (err) { setError(failure(err)); }
    finally { setBusy(false); }
  };

  // Откат — вернуть своё изменение — доступен и на паузе: часто паузу ставят как раз
  // из-за плохого результата. Нельзя только в «Наблюдении» — там в кабинет не пишем.
  const canRollback = item.execution_mode !== 'OBSERVE';
  const renderAction = action => <ActionDetails action={action} busy={busy} pending={pending}
    canAct={canAct} canRollback={canRollback} act={act} />;

  const tabs = [
    ['tactic', 'Тактика'],
    ['rk', 'Ставки РК'],
    ['products', 'Оборачиваемость'],
    ['stats', 'Статистика'],
    ['bids', `Ставки группой (ручное)${data?.bulk_groups?.length ? ` (${data.bulk_groups.length})` : ''}`],
    ['prices', 'Цены'],
    ['coinvest', 'Контроль соинвеста'],
    ['events', 'События'],
    ['log', 'Журнал'],
  ];

  return <section className='oz-auto__dashboard'>
    {error && <p role='alert' className='bidder__error'>{error}</p>}
    {!data ? <p>Загрузка статистики…</p> : <>
      <div className='oz-auto__tabs' role='tablist'>
        {tabs.map(([key, label]) => <button type='button' key={key} role='tab'
          aria-selected={tab === key} className={tab === key ? 'is-active' : ''}
          onClick={() => setTab(key)}>{label}</button>)}
        <span />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>Кто принимает решения
          <select value={item.execution_mode} disabled={busy}
            onChange={e => changeMode(e.target.value)}>
            {EXECUTION_MODES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <Help text={EXECUTION_MODE_HINT} /></label>
        <button type='button' disabled={busy} onClick={load}>Обновить</button>
      </div>

      {/* Остановка и препятствия видны на любой вкладке: пропустить их нельзя. */}
      {item.paused_reason && <div className='oz-auto__notice'>
        <strong>Автоматизация остановила себя</strong><p>{item.paused_reason}</p></div>}
      <BudgetApprovals item={item} base={base} request={request} onDone={load} />
      {data.issues.length > 0 && <div className='oz-auto__notice'>
        <strong>Требует внимания</strong><ul>{data.issues.map(text => <li key={text}>{text}</li>)}</ul></div>}

      {tab === 'tactic' && <TacticTab data={data} item={item} base={base} request={request}
        busy={busy} onCalibration={calibration} />}


      {tab === 'products' && <ProductsCard turnover={data.turnover} busy={busy} onSave={saveTurnover} />}

      {tab === 'bids' && <BulkBidCard groups={data.bulk_groups} busy={busy} canAct={canAct}
        status={item.status} constraints={item.constraints}
        onApply={(placement, bid) => act('bulk-bid', { placement, bid })} />}

      {tab === 'stats' && <>
      <StatsPanel base={base} request={request} days={days} setDays={setDays} fallback={data.series} />

      <details><summary>Данные по дням</summary><div className='oz-auto__table-scroll'><table>
        <thead><tr><th>Дата</th><th>Заказы</th><th>Сумма, ₽</th><th>Расход РК, ₽</th><th>Клики</th><th>Корзины</th><th>Прибыль, ₽</th><th>По дню спроса, ₽</th><th>Рент., %</th><th>Витрина, ₽</th><th>Соинвест, %</th><th>Соинвест, ₽/шт</th><th>Позиция</th><th>Экономика</th><th>Покрытие</th></tr></thead>
        <tbody>{data.series.map(day => <tr key={day.date}><td>{day.date}</td>
          {['orders', 'revenue', 'ad_spend', 'clicks', 'carts', 'profit', 'profit_by_demand'].map(key => <td key={key}>{number(day[key])}</td>)}
          <td>{day.margin_pct == null ? 'Нет данных' : percent(day.margin_pct)}</td>
          <td>{number(day.shelf_price)}</td>
          <td>{day.coinvest_pct == null ? '—' : percent(day.coinvest_pct)}</td>
          <td>{number(day.coinvest_per_unit)}</td>
          <td>{day.search_position == null ? 'Нет мониторинга' : number(day.search_position)}</td>
          <td>{day.econ_confidence === 'CONFIRMED' ? 'Факт'
            : day.econ_confidence === 'ESTIMATED' ? 'Оценка'
              : day.econ_missing?.length ? `Нет: ${day.econ_missing.join(', ')}` : 'Неполная'}</td>
          <td>{day.covered_skus}/{day.expected_skus} SKU; {day.covered_ad_pairs}/{day.expected_ad_pairs} пар</td></tr>)}</tbody>
      </table></div></details>
      {!data.position_monitoring && <p className='oz-auto__muted'>
        Мониторинг позиций по этим товарам не найден: цель лидерства опирается на
        показы<Help text='Позиция в выдаче — единственный прямой способ измерить лидерство. Без неё остаются показы, а они зависят и от бюджетов конкурентов.' /></p>}
      </>}

      {tab === 'bids' && <>
      <details className='oz-auto__targets'>
        <summary>Отдельные пары РК–товар
          <Help text='Кнопка «Задать ставку самому» нужна, только если вы хотите проверить своё значение вместо предложенного: система так же подтвердит его в OZON и будет вести окно наблюдения. Для обычной работы сюда заходить не нужно.' /></summary>
      <div className='oz-auto__table-scroll'><table><thead><tr><th>РК / товар</th><th>Размещение</th><th>Ставка</th><th>Последнее изменение</th><th>Готовность</th><th /></tr></thead><tbody>{data.targets.map(target => {
        const change = data.last_changes?.[target.key];
        const editing = proposal?.target.key === target.key;
        return [<tr key={target.key}>
          <td><strong>{target.campaign_name || target.campaign_id}</strong><small>{target.product_name || 'Товар без названия'}</small><small>Артикул: {target.offer_id || 'не указан'} · SKU {target.sku_id}</small></td>
          <td>{target.placement_label || target.placement}</td><td>{number(target.bid)} ₽</td>
          <td>{change ? <small>{fmtDateTime(change.at)} · {change.who}{change.before != null && ` · ${number(change.before)} → ${number(change.after)}`}</small> : <small>не менялась</small>}</td>
          <td>{target.available ? 'Доступна проверка' : target.reasons.join('. ')}<small>Синхронизация: {target.synced_at ? fmtDateTime(target.synced_at) : 'нет данных'}</small></td>
          <td><button type='button' disabled={busy || pending || !target.available || !canAct}
            title={!canAct
              ? 'Недоступно в режиме «Наблюдение»: в нём система ничего не отправляет. Смените режим выше.'
              : pending ? 'Идёт другая проверка — одновременно проверяется только одна гипотеза.'
                : !target.available ? 'Этой парой РК–товар сейчас управлять нельзя, причина в колонке «Готовность».'
                  : 'Задать конкретную ставку самому, вместо той, что предложила система. Дальше всё как обычно: подтверждение OZON и окно наблюдения.'}
            onClick={() => setProposal(editing ? null : { target, value: target.bid, actionId: crypto.randomUUID() })}>
            {editing ? 'Скрыть' : 'Задать ставку самому'}</button></td>
        </tr>,
        // Редактор открывается прямо под своей строкой: внизу длинного списка его не видно.
        editing && <tr key={`${target.key}-editor`} className='oz-auto__inline-editor'><td colSpan='6'>
          <div className='oz-auto__experiment'>
            <h4>Проверка: {proposal.target.campaign_name}</h4>
            <p>{proposal.target.product_name} · Артикул {proposal.target.offer_id || 'не указан'} · SKU {proposal.target.sku_id}</p>
            <p>Текущая ставка {number(proposal.target.bid)} ₽. Максимум {number(item.constraints?.max_bid)} ₽; шаг до {number(item.constraints?.bid_change_limit_pct)}%.</p>
            <label>Новая ставка, ₽ <input type='number' min='0.01' step='0.01' value={proposal.value} autoFocus onChange={e => setProposal({ ...proposal, value: e.target.value })} /></label>
            <p>После подтверждения OZON начнётся наблюдение. Реклама продолжает расходовать бюджет РК.</p>
            <button type='button' disabled={busy || !Number(proposal.value) || Number(proposal.value) === proposal.target.bid}
              onClick={() => act('experiments', { action_id: proposal.actionId, lever: 'BID', target_key: proposal.target.key, expected_bid: proposal.target.bid, requested_bid: Number(proposal.value) })}>
              {busy ? 'Проверяем и отправляем…' : 'Применить ставку и начать проверку'}</button>
            <button type='button' disabled={busy} onClick={() => setProposal(null)}>Отмена</button>
          </div></td></tr>,
        ];
      })}</tbody></table></div>
      {!canAct && <p className='oz-auto__muted'>Ручная проверка доступна в режимах
        «Автоматический» и «С подтверждением».</p>}
      </details>
      </>}

      {tab === 'prices' && <OzonAutomationPrices base={base} request={request} busy={busy} />}

      {tab === 'coinvest' && <OzonAutomationCoinvest base={base} request={request} busy={busy}
        mode={item.execution_mode} />}

      {tab === 'events' && <OzonAutomationMilestones base={base} request={request}
        targets={data.targets} busy={busy} />}

      {tab === 'log' && <>
        <details className='oz-auto__strategy-block' open>
          <summary><strong>Стратегия</strong></summary>
        <ReadinessCard readiness={data.readiness} />
        <StrategyGoal tactics={data.tactics} objective={item.objective} />
        <PlannerCard planner={planner} events={data.events} busy={busy} canAct={canAct}
          pending={pending} mode={item.execution_mode} status={item.status}
          onApply={applyPlanner} onEnableActing={enableActing}
          onRecalculate={() => act('cycle?apply=false')} />
        <GroupEconomics economics={data.group_economics} from={data.period_from} to={data.period_to} decision={data.decision_window} />
        <DeferredDemandCard deferred={data.deferred_demand} />
        </details>
        <JournalCard base={base} request={request} actions={data.actions}
          renderAction={renderAction} reloadKey={journalKey} />
      </>}

      {tab === 'rk' && <RkBidsTab base={base} request={request} targets={data.targets} lastChanges={data.last_changes} />}
    </>}
  </section>;
}

const TURNOVER_INPUTS = [
  ['reserve_units', 'Резерв, шт.'],
  ['replenish_period_days', 'Период пополнения, дн.'],
  ['replenish_qty', 'Кол-во пополнения, шт.'],
  ['target_turnover_days', 'Желаемая оборачиваемость, дн.'],
];
const TURNOVER_INPUT_KEYS = TURNOVER_INPUTS.map(([key]) => key);
const TURNOVER_LABEL = Object.fromEntries(TURNOVER_INPUTS);
const TURNOVER_STATUS_CLASS = { OK: 'is-ok', STOCKOUT_RISK: 'is-action', OVERSTOCK: 'is-waiting', UNKNOWN: 'is-optional' };
const PRODUCTS_SHEET = 'Оборачиваемость';
const XLSX_SKU = 'SKU';
const XLSX_OFFER = 'Артикул';
const XLSX_NAME = 'Товар';

// Only the SKU's own values are editable; an inherited (group or default)
// value shows as a grey placeholder, so it is never written back by accident.
const ownDraft = rows => Object.fromEntries((rows || []).map(row => [String(row.sku_id),
  Object.fromEntries(TURNOVER_INPUT_KEYS.map(key => [key,
    (row.own_inputs || []).includes(key) && row.inputs?.[key] != null ? String(row.inputs[key]) : '']))]));

const toNumberOrEmpty = raw => {
  const text = String(raw ?? '').replace(/\s/g, '').replace(',', '.').trim();
  if (text === '') return '';
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 ? String(value) : null;
};

export function ProductsCard({ turnover, busy, onSave }) {
  const sourceRows = useMemo(() => turnover?.rows || [], [turnover]);
  const [enabled, setEnabled] = useState(Boolean(turnover?.enabled));
  const [useFbs, setUseFbs] = useState(turnover?.use_fbs_stock !== false);
  const [draft, setDraft] = useState(() => ownDraft(sourceRows));
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkKey, setBulkKey] = useState(TURNOVER_INPUT_KEYS[0]);
  const [bulkValue, setBulkValue] = useState('');
  const [message, setMessage] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setEnabled(Boolean(turnover?.enabled));
    setUseFbs(turnover?.use_fbs_stock !== false);
    setDraft(ownDraft(turnover?.rows));
    setSelected([]);
  }, [turnover]);

  const initial = useMemo(() => ownDraft(sourceRows), [sourceRows]);
  const changedSkus = Object.keys(draft).filter(sku =>
    TURNOVER_INPUT_KEYS.some(key => (draft[sku]?.[key] ?? '') !== (initial[sku]?.[key] ?? '')));
  const flagsChanged = enabled !== Boolean(turnover?.enabled)
    || useFbs !== (turnover?.use_fbs_stock !== false);

  const change = (sku, key, value) => setDraft(current =>
    ({ ...current, [sku]: { ...current[sku], [key]: value } }));

  // Rows as the table sees them: every figure is a plain field, so search,
  // sort and the CSV export work on exactly what is on screen. Available stock
  // and turnover are recomputed live from the edited reserve and inputs.
  const rows = useMemo(() => sourceRows.map(row => {
    const sku = String(row.sku_id);
    const own = draft[sku] || {};
    const effective = key => (own[key] !== '' && own[key] != null ? Number(own[key])
      : row.inputs?.[key] ?? (key === 'target_turnover_days' ? row.target_days : null));
    const reserve = effective('reserve_units') || 0;
    const available = row.marketplace_units == null ? null : row.marketplace_units + reserve;
    const rate = row.sales_per_day;
    const qty = effective('replenish_qty');
    return {
      ...row,
      key: sku,
      reserve_units: effective('reserve_units'),
      replenish_period_days: effective('replenish_period_days'),
      replenish_qty: qty,
      target_turnover_days: effective('target_turnover_days'),
      available_units: available,
      cover_days: available != null && rate > 0 ? Math.round(available / rate * 10) / 10 : null,
      cover_after_replenish_days: available != null && rate > 0 && qty
        ? Math.round((available + qty) / rate * 10) / 10 : null,
    };
  }), [sourceRows, draft]);

  const matches = row => {
    return matchesSearch(row, search);
  };

  const applyBulk = skus => {
    const value = toNumberOrEmpty(bulkValue);
    if (value === null) {
      setMessage({ kind: 'error', text: 'Нужно неотрицательное число или пусто, чтобы очистить' });
      return;
    }
    if (!skus.length) { setMessage({ kind: 'error', text: 'Нет строк для заполнения' }); return; }
    setDraft(current => {
      const next = { ...current };
      skus.forEach(sku => { next[sku] = { ...next[sku], [bulkKey]: value }; });
      return next;
    });
    setMessage({ kind: 'ok',
      text: `«${TURNOVER_LABEL[bulkKey]}»: ${value === '' ? 'очищено' : value} у ${skus.length} товаров. Нажмите «Сохранить».` });
  };

  const downloadXlsx = () => {
    const data = rows.map(row => ({
      [XLSX_SKU]: row.sku_id, [XLSX_OFFER]: row.offer_id, [XLSX_NAME]: row.name,
      ...Object.fromEntries(TURNOVER_INPUT_KEYS.map(key => [TURNOVER_LABEL[key], draft[row.key]?.[key] ?? ''])),
    }));
    const sheet = XLSX.utils.json_to_sheet(data, {
      header: [XLSX_SKU, XLSX_OFFER, XLSX_NAME, ...TURNOVER_INPUT_KEYS.map(key => TURNOVER_LABEL[key])],
    });
    sheet['!cols'] = [{ wch: 14 }, { wch: 22 }, { wch: 60 }, ...TURNOVER_INPUT_KEYS.map(() => ({ wch: 24 }))];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, PRODUCTS_SHEET);
    XLSX.writeFile(book, 'bidder-oborachivaemost.xlsx', { bookType: 'xlsx' });
  };

  const importXlsx = event => {
    const file = event.target.files?.[0];
    event.target.value = '';  // otherwise picking the same file again does nothing
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const book = XLSX.read(ev.target.result, { type: 'array' });
        const sheet = book.Sheets[PRODUCTS_SHEET] || book.Sheets[book.SheetNames[0]];
        const lines = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        // A product is recognised by SKU; without it, by the seller article.
        const bySku = new Set(sourceRows.map(row => String(row.sku_id)));
        const byOffer = new Map(sourceRows.filter(row => row.offer_id)
          .map(row => [String(row.offer_id), String(row.sku_id)]));
        const next = {};
        let skipped = 0;
        lines.forEach(line => {
          const skuRaw = String(line[XLSX_SKU] ?? '').trim();
          const sku = bySku.has(skuRaw) ? skuRaw : byOffer.get(String(line[XLSX_OFFER] ?? '').trim());
          if (!sku) { if (skuRaw || line[XLSX_OFFER]) skipped += 1; return; }
          TURNOVER_INPUT_KEYS.forEach(key => {
            if (!(TURNOVER_LABEL[key] in line)) return;
            const value = toNumberOrEmpty(line[TURNOVER_LABEL[key]]);
            if (value === null) { skipped += 1; return; }
            next[sku] = { ...(next[sku] || {}), [key]: value };
          });
        });
        if (!Object.keys(next).length) {
          setMessage({ kind: 'error', text: 'В файле не нашлось товаров этой группы' });
          return;
        }
        setDraft(current => {
          const merged = { ...current };
          Object.entries(next).forEach(([sku, values]) => { merged[sku] = { ...merged[sku], ...values }; });
          return merged;
        });
        setMessage({ kind: 'ok',
          text: `Подставлено для ${Object.keys(next).length} товаров${skipped ? `, пропущено ${skipped}` : ''}. Проверьте и нажмите «Сохранить».` });
      } catch (err) {
        setMessage({ kind: 'error', text: 'Не удалось прочитать файл: ' + (err.message || err) });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const save = async () => {
    const ok = await onSave({
      enabled, use_fbs_stock: useFbs,
      per_sku: Object.fromEntries(changedSkus.map(sku => [sku, Object.fromEntries(
        TURNOVER_INPUT_KEYS.map(key => [key, draft[sku]?.[key] === '' ? null : Number(draft[sku]?.[key])]))])),
    });
    if (ok) setMessage({ kind: 'ok', text: 'Сохранено. Статусы пересчитаны.' });
  };

  const editable = key => ({
    key, header: TURNOVER_LABEL[key], type: 'number', width: 130, align: 'right',
    render: (_, row) => <input type='number' min='0' step='1' className='oz-auto__cell-input'
      value={draft[row.key]?.[key] ?? ''}
      placeholder={row[key] != null ? String(row[key]) : ''}
      onClick={e => e.stopPropagation()}
      onChange={e => change(row.key, key, e.target.value)} />,
  });

  const columns = [
    { key: 'name', header: 'Товар', width: 280, sticky: true,
      render: (_, row) => <div className='oz-auto__product-cell'><strong>{row.name || `SKU ${row.sku_id}`}</strong>
        <small>{row.offer_id || 'без артикула'} · {row.sku_id}</small></div> },
    { key: 'offer_id', header: 'Артикул', width: 130 },
    { key: 'sku_id', header: 'SKU', width: 120 },
    { key: 'status_label', header: 'Статус', width: 200,
      render: (_, row) => <div className={`oz-auto__turnover-status ${TURNOVER_STATUS_CLASS[row.status] || ''}`}
        title={row.reason}><strong>{row.status_label}</strong><small>{row.reason}</small></div> },
    { key: 'cover_days', header: 'Оборачиваемость, дн.', type: 'number', width: 130, align: 'right' },
    { key: 'cover_after_replenish_days', header: 'После прихода, дн.', type: 'number', width: 120, align: 'right' },
    ...TURNOVER_INPUT_KEYS.map(editable),
    { key: 'available_units', header: 'Доступно, шт.', type: 'number', width: 110, align: 'right' },
    { key: 'stock_fbo', header: 'FBO, шт.', type: 'number', width: 90, align: 'right' },
    { key: 'stock_fbs', header: useFbs ? 'FBS, шт.' : 'FBS, шт. (не учит.)', type: 'number', width: 110, align: 'right' },
    { key: 'sales_per_day', header: 'Заказов в день', type: 'number', width: 110, align: 'right' },
    { key: 'orders', header: 'Заказы за период', type: 'number', width: 110, align: 'right' },
    { key: 'revenue', header: 'Выручка, ₽', type: 'number', width: 120, align: 'right' },
    { key: 'ad_spend', header: 'Реклама, ₽', type: 'number', width: 110, align: 'right' },
  ];

  if (!sourceRows.length) return <p>В автоматизации нет товаров.</p>;
  const found = rows.filter(matches).map(row => row.key);
  const dirty = changedSkus.length > 0 || flagsChanged;
  return <div className='oz-auto__products-tab'>
    <div className='oz-auto__products-bar'>
      <label className='oz-auto__switch'><input type='checkbox' checked={enabled} onChange={e => setEnabled(e.target.checked)} />
        Учитывать оборачиваемость в автоматизации
        <Help text='Поверх выбранной цели: если запас кончится до прихода поставки — ставка вниз, цена вверх; если товар залёживается дольше желаемого — ставка вверх, цена вниз. Внутри коридора ±15% ничего не меняется. Ниже минимальной рентабельности система пойдёт только с вашего согласования.' /></label>
      <label className='oz-auto__switch'><input type='checkbox' checked={useFbs} onChange={e => setUseFbs(e.target.checked)} />
        Учитывать остатки FBS
        <Help text='Выключите, если остаток FBS в кабинете — условная цифра. Тогда везде, включая проверку «остаток закончился», считается только FBO.' /></label>
      <span />
      <button type='button' className='oz-auto__primary' disabled={busy || !dirty} onClick={save}>
        {dirty ? `Сохранить${changedSkus.length ? ` (${changedSkus.length})` : ''}` : 'Сохранено'}</button>
    </div>

    <div className='oz-auto__bulk'>
      <strong>Массовое заполнение</strong>
      <select value={bulkKey} onChange={e => setBulkKey(e.target.value)}>
        {TURNOVER_INPUTS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
      <input type='text' inputMode='decimal' placeholder='Значение (пусто — очистить)' value={bulkValue}
        onChange={e => setBulkValue(e.target.value)} />
      <button type='button' disabled={!selected.length} onClick={() => applyBulk(selected)}>
        К выбранным ({selected.length})</button>
      <button type='button' onClick={() => applyBulk(found)}>
        Ко всем {search.trim() ? 'найденным' : 'товарам'} ({found.length})</button>
    </div>

    {message && <p className={message.kind === 'error' ? 'bidder__error' : 'oz-auto__ok'} role='status'>{message.text}</p>}

    <UnifiedTable
      tableId='ozon-bidder-products'
      columns={columns}
      data={rows}
      getRowKey={row => row.key}
      search={search}
      onSearchChange={setSearch}
      selectable
      selectedRows={selected}
      onSelectionChange={setSelected}
      pageSizes={[50, 100, 250, 'all']}
      exportFilename='bidder-oborachivaemost'
      onExportCsv
      onCopyTable
      fullscreenEnabled
      toolbarActions={[
        { label: 'Скачать XLSX', onClick: downloadXlsx, title: 'Все товары с текущими значениями — заполните и загрузите обратно' },
        { label: 'Загрузить XLSX', onClick: () => fileRef.current?.click(), title: 'Товар находится по SKU, без него — по артикулу' },
      ]}
    />
    <input ref={fileRef} type='file' accept='.xlsx,.xls' style={{ display: 'none' }} onChange={importXlsx} />

    <p className='oz-auto__muted'>Доступно = остаток на маркетплейсе + резерв. Оборачиваемость = доступно / заказов в день.
      Серое значение в поле — общее для группы или по умолчанию; своё значение для товара его перекрывает.
      Доступно и оборачиваемость пересчитываются сразу, статус — после сохранения.
      Столбцы можно перетаскивать, менять ширину и скрывать через «Столбцы».
      {!enabled && ' Слой выключен: таблица считается, но на решения автоматизации не влияет.'}</p>
  </div>;
}

function StrategyGoal({ tactics, objective }) {
  if (!tactics?.goal) return null;
  const pct = Math.round((tactics.goal - 1) * 100);
  const target = objective?.tactic_growth_pct;
  const adjusted = objective?.tactic_adjusted_at;
  return <div className='oz-auto__planner'>
    <h4>Цель тактики
      <Help text='Стратегия раз в неделю сдвигает цель тактики: заказы выросли, а рентабельность не ниже минимума — цель на 20 п.п. выше, иначе ниже. Новая цель достигается постепенно за неделю, а не в ближайшие полчаса.' /></h4>
    <p>Сейчас тактика ведёт группу к уровню <strong>{pct >= 0 ? '+' : ''}{pct}%</strong> к обычному
      {target != null && Math.round(target) !== pct ? ` (идёт к ${Math.round(target)}%)` : ''}.
      {adjusted ? ` Последний пересмотр: ${fmtDate(adjusted)}, следующий — через неделю после него.` : ' Первый пересмотр — через неделю работы.'}</p>
  </div>;
}

function ActionDetails({ action, busy, pending, canAct, canRollback, act }) {
  return <article className='oz-auto__action'>
    <strong>{action.lever_label || LEVERS[action.lever] || action.lever}
      {' · '}{action.status_label || STATES[action.status] || action.status}
      {action.before != null && ` · ${number(action.before)} → ${number(action.requested)}`}</strong>
    <small>{fmtDateTime(action.requested_at)} · {action.target_key}
      {action.execution_mode === 'AUTO' ? ' · решила система' : action.execution_mode === 'MANUAL' ? ' · вручную' : ' · по вашему подтверждению'}</small>
    {action.evidence?.plan?.probabilities && <p className='oz-auto__why'>
      <strong>Гипотеза:</strong> {action.reason}</p>}
    {!action.evidence?.plan?.probabilities && <p>{action.reason}</p>}
    {action.observation_until && <p>Окно наблюдения до {action.observation_until}.</p>}
    {action.confirmed_at && <p>Подтверждено: {number(action.confirmed)} · {fmtDateTime(action.confirmed_at)}</p>}
    {action.evidence?.result && <ResultBlock result={action.evidence.result} />}
    {action.evidence?.plan?.probabilities && <p className='oz-auto__muted'>
      Ожидалось перед изменением: улучшение {percent(action.evidence.plan.probabilities.improve * 100)},
      ухудшение {percent(action.evidence.plan.probabilities.deteriorate * 100)},
      результат не установлен {percent(action.evidence.plan.probabilities.unclear * 100)}.</p>}
    {['APPLYING', 'UNCONFIRMED', 'OBSERVING'].includes(action.status) && action.lever === 'BID'
      && <button type='button' disabled={busy} onClick={() => act(`actions/${action.id}/reconcile`)}>Сверить с OZON</button>}
    {action.status === 'OBSERVING' && <button type='button' disabled={busy} onClick={() => act(`actions/${action.id}/finish`)}>Завершить наблюдение, оставить как есть</button>}
    {action.status === 'UNCONFIRMED' && <button type='button' disabled={busy} onClick={() => act(`actions/${action.id}/finish`)}>Закрыть без оценки</button>}
    {['COMPLETED', 'INTERRUPTED'].includes(action.status) && action.lever !== 'INFOGRAPHIC' && action.before != null
      && <button type='button' disabled={busy || pending || !(canRollback ?? canAct)} onClick={() => act(`actions/${action.id}/rollback`)}>Вернуть прежнее значение</button>}
  </article>;
}

function ReadinessCard({ readiness }) {
  const [showDone, setShowDone] = useState(false);
  if (!readiness?.steps?.length) return null;
  const steps = [...readiness.steps].sort(
    (a, b) => READY_ORDER.indexOf(a.status) - READY_ORDER.indexOf(b.status));
  const visible = showDone ? steps : steps.filter(step => step.status !== 'OK');
  return <div className={`oz-auto__readiness ${readiness.ready ? 'is-ready' : ''}`}>
    <h4>Что дальше</h4>
    <p className='oz-auto__readiness-headline'>{readiness.headline}</p>
    <ul>
      {visible.map(step => {
        const [label, className] = READY[step.status] || ['', ''];
        return <li key={step.key} className={className}>
          <span className='oz-auto__readiness-tag'>{label}</span>
          <div>
            <strong>{step.title}</strong>
            <small>{step.detail}</small>
            {step.action && <em>{step.action}</em>}
          </div>
        </li>;
      })}
    </ul>
    {steps.some(step => step.status === 'OK') && <button type='button'
      onClick={() => setShowDone(value => !value)}>
      {showDone ? 'Скрыть выполненное' : `Показать выполненное (${steps.filter(s => s.status === 'OK').length})`}
    </button>}
  </div>;
}

function ResultBlock({ result }) {
  return <div>
    <p>{result.note} Итог: <strong>{RESULTS[result.classification] || 'неясно'}</strong>. {result.reason}</p>
    {result.goal_label && <p>Цель измерялась в: {result.goal_label}.
      {result.before_goal_per_day != null && ` Было ${number(result.before_goal_per_day)}, стало ${number(result.after_goal_per_day)}.`}</p>}
    {result.before_per_day && <p>Заказы в день: {number(result.before_per_day.orders)} → {number(result.after_per_day.orders)}.
      Прибыль в день: {number(result.before_per_day.profit)} → {number(result.after_per_day.profit)} ₽.
      Расход РК в день: {number(result.before_per_day.ad_spend)} → {number(result.after_per_day.ad_spend)} ₽.</p>}
    {result.before_per_day?.coinvest_per_unit != null && <p>Соинвест на единицу:
      {' '}{number(result.before_per_day.coinvest_per_unit)} → {number(result.after_per_day.coinvest_per_unit)} ₽.
      {result.coinvest_shift_pct != null && ' Это самостоятельная причина изменения спроса.'}</p>}
    {result.before_per_day?.search_position != null && <p>Позиция в поиске:
      {' '}{number(result.before_per_day.search_position)} → {number(result.after_per_day.search_position)}.</p>}
    {result.interval && <p>Интервал изменения: {result.interval.map(number).join(' … ')}.
      Значимый порог: {number(result.meaningful_delta)}. Метод: {result.aggregation === 'weekly_mean' ? 'сравнение полных недель' : 'сравнение дней'}.</p>}
    {result.interference?.length > 0 && <p>Внутри окна менялись и другие рычаги: {result.interference.map(hit => `${LEVERS[hit.lever] || hit.lever} (${hit.date})`).join(', ')}.</p>}
  </div>;
}

function PlannerCard({ planner, events, busy, canAct, pending, mode, status,
                      onApply, onEnableActing, onRecalculate }) {
  const [open, setOpen] = useState(false);
  if (!planner) return null;
  const blocking = (events || []).filter(event => event.severity !== 'INFO');
  const target = planner.target || {};
  return <div className='oz-auto__planner'>
    <h4>Следующая гипотеза</h4>
    {planner.decision === 'PROPOSE' ? <>
      <p><strong>{LEVERS[planner.lever] || planner.lever}</strong>
        {planner.requested_value != null && ` · ${number(planner.previous_value)} → ${number(planner.requested_value)}`}
        {planner.direction === 'UP' ? ' (вверх)' : planner.direction === 'DOWN' ? ' (вниз)' : ''}</p>
      <p>{target.campaign_name ? `${target.campaign_name} · ` : ''}{target.product_name || `SKU ${target.sku_id}`}</p>
      <p>{planner.reason}</p>
      <p>Окно наблюдения: {planner.window?.min_days}–{planner.window?.max_days} дней.
        Значимый порог: {planner.window?.meaningful_change_pct}%. Цель: {planner.goal_label}.</p>
      <p>Вероятности: улучшение {percent(planner.probabilities.improve * 100)},
        ухудшение {percent(planner.probabilities.deteriorate * 100)},
        неясно {percent(planner.probabilities.unclear * 100)}. Качество данных: {planner.confidence_label || CONFIDENCE[planner.confidence] || planner.confidence}.</p>
      {mode === 'AUTO' && planner.requires_approval
        ? <>
          <p className='oz-auto__muted'>Этот шаг опускает рентабельность ниже заданного минимума,
            поэтому сама система его не отправит — только после вашего согласования.</p>
          <button type='button' className='oz-auto__primary'
            disabled={busy || pending || !canAct} onClick={onApply}>
            Согласовать и применить</button>
        </>
        : mode === 'AUTO'
        ? <p className='oz-auto__muted'>В автономном режиме это действие будет отправлено ближайшим циклом.</p>
        : mode === 'OBSERVE'
          ? <>
            <p className='oz-auto__muted'>Сейчас режим «Наблюдение»: система считает и предлагает,
              но в кабинет ничего не отправляет. Чтобы применить эту гипотезу, включите режим
              «С подтверждением» — отправка всё равно останется за вами.</p>
            <button type='button' className='oz-auto__primary' disabled={busy || status !== 'ACTIVE'}
              onClick={onEnableActing}>Включить режим «С подтверждением»</button>
          </>
          : <>
            <button type='button' className='oz-auto__primary'
              disabled={busy || pending || !canAct} onClick={onApply}>
              Применить эту гипотезу</button>
            {pending && <p className='oz-auto__muted'>Сначала завершите текущую проверку:
              одновременно проверяется только одна гипотеза.</p>}
            {status !== 'ACTIVE' && <p className='oz-auto__muted'>Автоматизация на паузе —
              возобновите её, чтобы отправлять изменения.</p>}
          </>}
    </> : <p>{planner.decision === 'PAUSE' ? 'Автоматизация остановлена: ' : 'Сейчас действий нет: '}{planner.reason}</p>}
    {planner.details?.length > 0 && <ul className='oz-auto__muted'>{planner.details.map(text => <li key={text}>{text}</li>)}</ul>}

    <div className='oz-auto__planner-actions'>
      <button type='button' onClick={() => setOpen(value => !value)}>
        {open ? 'Скрыть разбор' : 'Показать карту вероятностей и события'}</button>
      {/* Пересчёт относится к этой гипотезе, поэтому стоит рядом с ней. */}
      {onRecalculate && <button type='button' disabled={busy} onClick={onRecalculate}>
        Пересчитать</button>}
    </div>
    {open && <div className='oz-auto__probability'>
      {blocking.length > 0 && <><h5>События, ограничивающие действия</h5>
        <ul>{blocking.map((event, index) => <li key={`${event.code}-${index}`}>
          <strong>{SEVERITY[event.severity] || event.severity}:</strong> {event.message}</li>)}</ul></>}
      {planner.probability_map?.levers && <><h5>Карта вероятностей по рычагам</h5>
        <div className='oz-auto__table-scroll'><table>
          <thead><tr><th>Рычаг</th><th>Улучшение</th><th>Ухудшение</th><th>Неясно</th><th>Почему</th></tr></thead>
          <tbody>{planner.probability_map.levers.map(lever => <tr key={lever.lever}>
            <td>{LEVERS[lever.lever] || lever.lever}</td>
            {Object.keys(OUTCOMES).map(key => <td key={key}>{percent(lever.probabilities[key] * 100)}</td>)}
            <td>{lever.blocked_reason
              ? <em>{lever.blocked_reason}</em>
              : <ul>{lever.signals.map(signal => <li key={signal.code}>{signal.explanation}</li>)}
                {!lever.signals.length && <li>Особых сигналов нет: базовая неопределённость</li>}</ul>}</td>
          </tr>)}</tbody></table></div>
        <p className='oz-auto__muted'>{planner.probability_map.note}</p></>}
    </div>}
  </div>;
}

// Бюджет РК после провала или скачка выручки ждёт согласования: видно на любой вкладке.
export function BudgetApprovals({ item, base, request, onDone }) {
  const [busy, setBusy] = useState('');
  const [done, setDone] = useState({});
  const [error, setError] = useState('');
  const campaigns = item?.objective?.budget?.campaigns || {};
  const pending = Object.entries(campaigns)
    .filter(([, entry]) => entry?.anomaly && !entry.anomaly.approved && !done[entry.anomaly.target]);
  if (!pending.length) return null;
  const approve = async (campaignId, target) => {
    setBusy(campaignId); setError('');
    try {
      await request(`${base}/budget/${campaignId}/approve`, { method: 'POST' });
      setDone(value => ({ ...value, [target]: true }));
      if (onDone) await onDone();
    } catch (err) {
      setError(typeof err?.detail === 'string' ? err.detail : err?.message || 'Не удалось согласовать бюджет');
    } finally { setBusy(''); }
  };
  return <div className='oz-auto__notice is-action'>
    <strong>Бюджет РК ждёт согласования</strong>
    {error && <p role='alert' className='bidder__error'>{error}</p>}
    <ul>{pending.map(([campaignId, { anomaly }]) => <li key={campaignId}>
      РК «{anomaly.name || campaignId}»: {anomaly.kind === 'collapse' ? 'провал' : 'скачок'} выручки{' '}
      {Math.round(anomaly.change * 100)}%. Недельный бюджет {Math.round(anomaly.current)} → {Math.round(anomaly.target)} ₽.{' '}
      <button type='button' disabled={busy === campaignId}
        onClick={() => approve(campaignId, anomaly.target)}>Согласовать</button>
    </li>)}</ul>
  </div>;
}

const STAT_COLORS = ['#2563eb', '#7c3aed', '#16a34a', '#ea580c', '#dc2626', '#0f766e', '#f59e0b', '#475569'];
const STAT_DEFAULT = ['orders', 'clicks', 'carts', 'drr_ad', 'drr_total'];
const readStatChoice = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('oz-auto-stat-metrics') || 'null');
    return Array.isArray(saved) && saved.length ? saved.filter(key => METRICS[key]) : STAT_DEFAULT;
  } catch { return STAT_DEFAULT; }
};

// Статистика: показатели выбирает пользователь галочками; всё по группе или по одному товару.
export function StatsPanel({ base, request, days, setDays, fallback }) {
  const [chosen, setChosen] = useState(readStatChoice);
  const [allProducts, setAllProducts] = useState(true);
  const [sku, setSku] = useState('');
  const [products, setProducts] = useState([]);
  const [series, setSeries] = useState(fallback || []);
  const [error, setError] = useState('');
  const toggle = key => setChosen(current => {
    const next = current.includes(key) ? current.filter(item => item !== key) : [...current, key];
    try { localStorage.setItem('oz-auto-stat-metrics', JSON.stringify(next)); } catch { /* per-viewer convenience only */ }
    return next;
  });
  const wanted = allProducts ? '' : sku;
  useEffect(() => {
    let alive = true;
    request(`${base}/stats-series?days=${days}${wanted ? `&sku_id=${wanted}` : ''}`)
      .then(result => {
        if (!alive) return;
        setSeries(result.series || []); setProducts(result.products || []); setError('');
      })
      .catch(err => { if (alive) setError(failure(err)); });
    return () => { alive = false; };
  }, [request, base, days, wanted]);
  // The first product is the one shown when «все товары» is switched off.
  useEffect(() => {
    if (!sku && products.length) setSku(String(products[0].sku_id));
  }, [products, sku]);
  const keys = Object.keys(METRICS).filter(key => chosen.includes(key));
  const panels = keys.map((key, index) => ({
    key, label: METRICS[key][0], unit: METRICS[key][1], color: STAT_COLORS[index % STAT_COLORS.length],
  }));
  const points = series.map(day => ({ ...day, id: day.date, label: day.date }));
  return <div className='oz-auto__stats'>
    <div className='oz-auto__chart-controls'>
      <label>Период <select value={days} onChange={e => setDays(Number(e.target.value))}>{[14, 30, 60, 90].map(value => <option key={value} value={value}>{value} дней</option>)}</select></label>
      <label><input type='checkbox' checked={allProducts} onChange={e => setAllProducts(e.target.checked)} /> Все товары группы</label>
      {!allProducts && <label>Товар <select value={sku} onChange={e => setSku(e.target.value)}>
        {products.map(product => <option key={product.sku_id} value={product.sku_id}>
          {product.name}{product.offer_id ? ` · ${product.offer_id}` : ''}</option>)}</select></label>}
      <Help text='Завершённые дни по Москве. Пропуск в линии означает отсутствие строк, а не нулевой результат. Отметьте галочками нужные показатели: каждый рисуется своей панелью на общей оси дат.' />
    </div>
    {error && <p role='alert' className='bidder__error'>{error}</p>}
    <fieldset className='oz-auto__metric-choice'>
      <legend>Показатели на графике</legend>
      {Object.entries(METRICS).map(([key, [label]]) => <label key={key}>
        <input type='checkbox' checked={chosen.includes(key)} onChange={() => toggle(key)} /> {label}</label>)}
    </fieldset>
    {panels.length === 0
      ? <p className='oz-auto__muted'>Отметьте хотя бы один показатель.</p>
      : <MetricChart points={points} series={panels} />}
  </div>;
}

// Ставки РК: настройки блоков, антиканнибализм и пары по типам размещения.
export function RkBidsTab(props) {
  return <OzonAutomationRk {...props} />;
}

export function EfficiencyChart({ series, actions, metric }) {
  const [hover, setHover] = useState(null);
  const values = series.map(day => day[metric]).filter(value => value != null);
  if (!values.length) return <div className='oz-auto__chart-empty'>Нет полных данных для выбранного показателя. Проверьте синхронизацию статистики.</div>;
  const max = Math.max(1, ...values);
  const x = index => 55 + index * 870 / Math.max(1, series.length - 1);
  const y = value => 210 - value / max * 180;
  const segments = [];
  let segment = [];
  series.forEach((day, index) => {
    if (day[metric] == null) { if (segment.length) segments.push(segment); segment = []; }
    else segment.push(`${x(index)},${y(day[metric])}`);
  });
  if (segment.length) segments.push(segment);
  const selected = hover == null ? null : series[hover];
  return <div className='oz-auto__chart'>
    <svg viewBox='0 0 960 250' role='img' aria-label={METRICS[metric][0]}>
      {[0, .5, 1].map(fraction => <g key={fraction}><line x1='55' x2='925' y1={y(max * fraction)} y2={y(max * fraction)} stroke='#dbe2ea' /><text x='50' y={y(max * fraction) + 4} textAnchor='end' fontSize='11' fill='currentColor'>{number(max * fraction)}</text></g>)}
      {segments.map((points, index) => <polyline key={index} points={points.join(' ')} fill='none' stroke='#2563eb' strokeWidth='2' />)}
      {series.map((day, index) => day[metric] != null && <circle key={day.date} cx={x(index)} cy={y(day[metric])} r='3' fill='#2563eb'><title>{day.date}: {number(day[metric])}</title></circle>)}
      {actions.filter(action => action.confirmed_at).map(action => {
        const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(action.confirmed_at));
        const get = name => parts.find(part => part.type === name).value;
        const day = `${get('year')}-${get('month')}-${get('day')}`;
        const index = series.findIndex(item => item.date === day);
        return index < 0 ? null : <line key={action.id} x1={x(index)} x2={x(index)} y1='20' y2='212' stroke='#d97706' strokeDasharray='5 3'><title>{LEVERS[action.lever] || action.lever}: {action.before} → {action.confirmed}</title></line>;
      })}
      {series.map((day, index) => <rect key={day.date} x={x(index) - 435 / series.length} y='15' width={870 / series.length} height='205' fill='transparent' onMouseEnter={() => setHover(index)} onFocus={() => setHover(index)} tabIndex='0' aria-label={`${day.date}: ${number(day[metric])}`} />)}
      <text x='55' y='238' fontSize='12' fill='currentColor'>{series[0]?.date}</text><text x='925' y='238' textAnchor='end' fontSize='12' fill='currentColor'>{series[series.length - 1]?.date}</text>
    </svg>
    <div aria-live='polite'>{selected ? `${selected.date} · ${number(selected[metric])} ${METRICS[metric][1]}` : 'Наведите на день или выберите точку клавишей Tab'}</div>
  </div>;
}


// Окно, по которому защита ставки принимает решение (7 дней, по дню заказа): та же цифра, что в «ставку не поднимаю».
// Блок выше считает по начислениям за период дашборда, поэтому цифры могут сильно отличаться.
function DecisionWindow({ decision }) {
  const main = decision?.windows?.[String(decision.days)];
  if (!main) return null;
  const breach = decision.state === 'breach';
  const other = ['14', '28'].filter(key => decision.windows[key]);
  return <div className={`oz-auto__decision${breach ? ' is-breach' : ''}`} aria-label='Проверка для ставки за 7 дней'>
    <div className='oz-auto__economics-row'>
      <span><small>За {decision.days} дн. — по ним решается ставка</small><strong>{number(main.profit)} ₽ прибыли</strong></span>
      <span title='Прибыль ÷ себестоимость за эти дни, по дню заказа (оценка)'><small>Рентабельность{decision.floor != null ? ` (минимум ${percent(decision.floor)})` : ''}</small>
        <strong>{percent(main.margin_pct)}</strong></span>
      <span><small>Выручка</small><strong>{number(main.revenue)} ₽</strong></span>
      <span><small>Реклама</small><strong>{number(main.ads)} ₽</strong></span>
      <span><small>ДРР</small><strong>{main.drr == null ? 'Нет данных' : percent(main.drr * 100)}</strong></span>
    </div>
    {(decision.note || decision.cut) && <p className='oz-auto__muted'>{[decision.note, decision.cut].filter(Boolean).join('; ')}.</p>}
    {other.length > 0 && <p className='oz-auto__muted'>{other.map(key => `за ${key} дн.: прибыль ${number(decision.windows[key].profit)} ₽, рентабельность ${percent(decision.windows[key].margin_pct)}`).join(' · ')}</p>}
  </div>;
}

function GroupEconomics({ economics, from, to, decision }) {
  // Profit is not recomputed here: it comes from the unit economics module, so
  // the group and the «Экономика товаров» report cannot disagree.
  if (!economics) return null;
  const period = from && to ? ` · ${from} — ${to}` : '';
  return <div className='oz-auto__economics'>
    <h4>Экономика группы{period}
      <Help text='Источник — «Экономика → Unit-экономика → Экономика товаров» по товарам этой группы. Считается от цены продавца; соинвест OZON в прибыль не входит.' /></h4>
    {economics.known ? <>
      <div className='oz-auto__economics-row'>
        <span><small>Прибыль</small><strong>{number(economics.profit)} ₽</strong></span>
        <span title='Прибыль ÷ себестоимость — как в «Экономике»'><small>Рентабельность</small><strong>{percent(economics.margin_pct)}</strong></span>
        <span title='Прибыль ÷ выручка'><small>Маржинальность</small><strong>{percent(economics.sales_margin_pct)}</strong></span>
        <span><small>Выручка</small><strong>{number(economics.revenue)} ₽</strong></span>
        <span><small>Реклама</small><strong>{number(economics.ads)} ₽</strong></span>
        <span><small>Продано</small><strong>{number(economics.units)} шт.</strong></span>
      </div>

    </> : <>
      <p>Прибыль по группе не посчитана.
        {economics.missing_cogs?.length
          ? ` Не заполнена себестоимость: ${economics.missing_cogs.slice(0, 8).join(', ')}${economics.missing_cogs.length > 8 ? ` и ещё ${economics.missing_cogs.length - 8}` : ''}.`
          : ' За период нет продаж по этим товарам.'}</p>
      {economics.missing_cogs?.length > 0 && <p className='oz-auto__muted'>Себестоимость
        заполняется в разделе «Экономика → Unit-экономика → Себестоимость». Одного товара без
        неё достаточно, чтобы сумма по группе была занижена, поэтому она не показывается вовсе.</p>}
    </>}
    <DecisionWindow decision={decision} />
  </div>;
}

const LAG_LEVELS = { SKU: 'свой', MODEL: 'по модели', ACCOUNT: 'по кабинету', NONE: 'нет данных' };

function DeferredDemandCard({ deferred }) {
  if (!deferred?.lag) return null;
  const lag = deferred.lag;
  const days = value => value == null ? '—' : `${value} дн.`;
  const levels = Object.entries(lag.cart_levels || {})
    .map(([level, count]) => `${LAG_LEVELS[level] || level}: ${count}`).join(', ');
  return <div className='oz-auto__economics'>
    <h4>Отложенный спрос
      <Help text={'Считается по истории кабинета раз в сутки, по каждому артикулу; при малой истории — по модели или кабинету. '
        + (deferred.note || '')} /></h4>
    <div className='oz-auto__economics-row'>
      <span title='Сколько заказов приходит на одну корзину'><small>Корзина → заказ</small><strong>{lag.cart_rate == null ? '—' : number(lag.cart_rate * 100) + '%'}</strong></span>
      <span title='Доля заказов из корзины, оформленных в тот же день'><small>В тот же день</small><strong>{lag.cart_same_day == null ? '—' : percent(lag.cart_same_day * 100)}</strong></span>
      <span title='За сколько дней из корзин приходит 80% заказов. Окно наблюдения биддера не короче этого срока'><small>80% заказов за</small><strong>{days(lag.cart_p80_days)}</strong></span>
      <span title='Доля заказов, которые выкупают'><small>Выкуп</small><strong>{lag.buyout_rate == null ? '—' : percent(lag.buyout_rate * 100)}</strong></span>
      <span title='Через сколько дней после заказа приходит половина / 80% / 95% выкупов'><small>Выкуп через</small><strong>{lag.buyout_p50_days ?? '—'} / {lag.buyout_p80_days ?? '—'} / {lag.buyout_p95_days ?? '—'} дн.</strong></span>
    </div>
    <div className='oz-auto__economics-row'>
      <span title='Заказы, которые ещё будут выкуплены'><small>Выкупов в пути</small><strong>{number(deferred.buyouts_due_units)} шт. · {number(deferred.buyouts_due_rub)} ₽</strong></span>
      <span title='Заказы, которые ещё придут из уже положенных корзин'><small>Заказов из корзин</small><strong>{number(deferred.orders_due_from_carts)} шт.</strong></span>
      <span title='Реклама недели к выручке спроса, купленного в эти дни'><small>ДРР по дню спроса</small><strong>{deferred.drr_by_demand_7d == null ? '—' : percent(deferred.drr_by_demand_7d * 100)}</strong></span>
      {lag.backlog_units != null && <span title='Корзины, которые не стали заказами и ждут повода — обычно падения витрины'><small>Накоплено корзин</small><strong>{number(lag.backlog_units)} шт.</strong></span>}
      {lag.release_potential_units != null && <span title={`День падения витрины обычно забирает ${percent((lag.release_model?.share_low || 0) * 100)}–${percent((lag.release_model?.share_high || 0) * 100)} накопленного. Это заказы, которые съедят остаток`}><small>Потенциал выброса</small><strong>≈{number(lag.release_potential_units)} шт.</strong></span>}
    </div>
    {deferred.forecast && <div className='oz-auto__economics-row'>
      <span title='Прогноз заказов группы на 7 дней: уровень последних недель с поправкой на день недели и корзины в пути'><small>Прогноз заказов, 7 дн.</small><strong>{number(deferred.forecast.orders.reduce((a, b) => a + b, 0))} шт.</strong></span>
      <span title='Прогноз выкупов на 14 дней: прошлые и прогнозные заказы × доля выкупа × задержка'><small>Прогноз выкупов, 14 дн.</small><strong>{number(deferred.forecast.buyouts.reduce((a, b) => a + b, 0))} шт.</strong></span>
      {deferred.forecast_accuracy && <span title='Средняя ошибка последних прогнозов группы, сверенных с фактом'><small>Точность прогноза</small><strong>{deferred.forecast_accuracy.orders_error == null ? '—' : `±${percent(deferred.forecast_accuracy.orders_error * 100)}`}</strong></span>}
    </div>}
    <p className='oz-auto__muted'>Источник корзин: {lag.cart_source === 'ADS' ? 'реклама (полной воронки нет)' : lag.cart_source === 'STATS' ? 'полная воронка' : 'нет'}{levels ? ` · коэффициенты: ${levels}` : ''}</p>
  </div>;
}

function BulkBidCard({ groups, busy, canAct, status, constraints, onApply }) {
  const [edit, setEdit] = useState(null);
  const [result, setResult] = useState(null);
  if (!groups?.length) return null;
  const max = constraints?.max_bid;
  return <div className='oz-auto__bulk'>
    <h4>Ставки по размещениям
      <Help text='Кампании собраны по размещению: только такие ставки сравнимы между собой. Одно значение уходит во все кампании размещения, с теми же границами, что у автоматизации, включая персональные по товарам. Каждое изменение попадает в журнал.' /></h4>
    <div className='oz-auto__table-scroll'><table>
      <thead><tr><th>Размещение</th><th>Кампаний</th><th>Средняя ставка</th><th>Разброс</th><th>Минимум OZON</th><th /></tr></thead>
      <tbody>{groups.map(group => [<tr key={group.placement}>
        <td><strong>{group.placement_label}</strong>
          {group.blocked.length > 0 && <small>Недоступно кампаний: {group.blocked.length}</small>}</td>
        <td>{group.campaigns}{group.with_bid < group.campaigns && <small>со ставкой: {group.with_bid}</small>}</td>
        <td>{group.avg_bid == null ? 'Нет данных' : `${number(group.avg_bid)} ₽`}</td>
        <td>{group.min_bid == null ? '—' : `${number(group.min_bid)} … ${number(group.max_bid)} ₽`}</td>
        <td>{group.ozon_min_bid == null ? '—' : `${number(group.ozon_min_bid)} ₽`}</td>
        <td><button type='button' disabled={busy || !canAct || !group.available}
          title={!canAct ? 'Доступно в режимах «Автоматический» и «С подтверждением»'
            : !group.available ? 'Ни одной доступной кампании в этом размещении' : ''}
          onClick={() => setEdit(edit?.placement === group.placement ? null : {
            placement: group.placement, label: group.placement_label, value: group.avg_bid ?? '', group })}>
          {edit?.placement === group.placement ? 'Скрыть' : 'Изменить ставку группы'}</button></td>
      </tr>,
      edit?.placement === group.placement && <tr key={`${group.placement}-editor`} className='oz-auto__inline-editor'><td colSpan='6'>
        <div className='oz-auto__experiment'>
          <h4>{edit.label}: {edit.group.available} кампаний</h4>
          <p>Сейчас средняя ставка {edit.group.avg_bid == null ? 'неизвестна' : `${number(edit.group.avg_bid)} ₽`}
            {edit.group.min_bid != null && `, разброс ${number(edit.group.min_bid)} … ${number(edit.group.max_bid)} ₽`}.
            {max ? ` Максимум по автоматизации ${number(max)} ₽.` : ''}</p>
          <label>Новая ставка для всех, ₽ <input type='number' min='0.01' step='0.01' value={edit.value} autoFocus
            onChange={e => setEdit({ ...edit, value: e.target.value })} /></label>
          <p className='oz-auto__muted'>Кампания, которая не примет значение, будет названа отдельно —
            молча пропущена не будет. Окно наблюдения для ручного изменения не ведётся: это ваше
            решение, а не гипотеза системы.</p>
          <button type='button' className='oz-auto__primary' disabled={busy || !Number(edit.value)}
            onClick={async () => {
              const response = await onApply(edit.placement, Number(edit.value));
              if (response) { setResult(response); setEdit(null); }
            }}>
            {busy ? 'Отправляем…' : 'Применить ко всем кампаниям размещения'}</button>
          <button type='button' disabled={busy} onClick={() => setEdit(null)}>Отмена</button>
        </div></td></tr>,
      ])}</tbody>
    </table></div>
    {!canAct && <p className='oz-auto__muted'>В режиме «Наблюдение» система ничего не отправляет.</p>}
    {status !== 'ACTIVE' && <p className='oz-auto__muted'>Автоматизация на паузе — изменения не отправляются.</p>}

    {result && <div className='oz-auto__notice'>
      <strong>{result.placement_label}: {result.summary}</strong>
      {result.applied?.length > 0 && <ul>{result.applied.map(row => <li key={row.key}>
        {row.campaign_name || row.key}: {number(row.before)} → {number(row.after)} ₽</li>)}</ul>}
      {result.refused?.length > 0 && <>
        <p>Без изменений:</p>
        <ul>{result.refused.map(row => <li key={row.key}>
          {row.campaign_name || row.key}: {row.reason}</li>)}</ul></>}
      <button type='button' onClick={() => setResult(null)}>Понятно</button>
    </div>}
  </div>;
}
